import {
  Fragment,
  MouseEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import moment from 'moment';
import {
  acceptMyOrder,
  fetchMyAssignedProjects,
  fetchMyOrder,
  fetchMyOrders,
  rejectMyOrder,
} from './api';
import ExpiredOrderAlert from './ExpiredOrderAlert';
import OrderAccess from './OrderAccess';
import OrderGroupTabs from './OrderGroupTabs';
import OrderRowDetails from './OrderRowDetails';
import OrderStatusCell from './OrderStatusCell';
import PageHeader from './PageHeader';
import RowExpandToggle from './RowExpandToggle';
import {
  ApiResult,
  AssignedProject,
  OrderDetails,
  OrderFilters,
  OrderSummary,
} from './types';
import useQueryState from './useQueryState';
import {
  canConsultantAccept,
  canConsultantReject,
  DEFAULT_ORDER_GROUP,
  getActionErrorDetail,
  getActionErrorMessage,
  getClientProjectNames,
  isOrderGroup,
  isPastDecisionDeadline,
  ORDER_GROUP_STATUSES,
  ORDER_GROUPS,
  OrderGroup,
} from './utils';
import WaitingOrderPanel, { WaitingOrderDetails } from './WaitingOrderPanel';
import * as Styles from './Orders.css';

const EMPTY_PAGE = { content: [] as OrderSummary[], totalElements: 0 };
const SEARCH_DELAY_MS = 400;

// B2B and civil-contract orders waiting for the consultant's decision; they
// open expanded with the decision under their conditions. Every other row
// expands on click with the details of its detail page (change request of
// 12.10).
const isWaitingForDecision = (order: OrderSummary) =>
  canConsultantAccept(order) || canConsultantReject(order);

const MyOrdersContent = () => {
  const intl = useIntl();
  const [params, updateParams] = useQueryState();
  const tabParam = params.get('tab');
  const appliedSearch = params.get('q') || '';
  const page = Number(params.get('page')) || 0;
  const size = Number(params.get('size')) || 20;
  const [search, setSearch] = useState(appliedSearch);
  const [pendingCount, setPendingCount] = useState<number | null>(null);
  const [orders, setOrders] = useState(EMPTY_PAGE);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'unauthorized' | 'request' | null>(null);
  const [busyOrder, setBusyOrder] = useState('');
  const [message, setMessage] = useState<{
    severity: 'success' | 'error';
    id: string;
    detail?: string;
  } | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [projects, setProjects] = useState<AssignedProject[] | null>(null);
  // Details of waiting orders by number, loaded once per page visit; only a
  // decided order is dropped, so reloads, collapsing and reopening reuse them.
  const [details, setDetails] = useState<Record<string, WaitingOrderDetails>>(
    {},
  );
  const projectsRequested = useRef(false);
  const mounted = useRef(true);
  const latestRequest = useRef(0);
  const hasWaitingOrder = orders.content.some(isWaitingForDecision);

  // Open on the orders that need a decision; fall back to Active when there
  // are none.
  let group: OrderGroup | null = null;
  if (isOrderGroup(tabParam)) {
    group = tabParam;
  } else if (pendingCount !== null) {
    group = pendingCount > 0 ? 'pending' : DEFAULT_ORDER_GROUP;
  }

  const loadPendingCount = useCallback(async () => {
    const result = await fetchMyOrders({ status: ['PENDING'] }, 0, 1);
    setPendingCount(result.ok && result.data ? result.data.totalElements : 0);
  }, []);

  useEffect(() => {
    loadPendingCount();
  }, [loadPendingCount]);

  const load = useCallback(async () => {
    if (!group) {
      return;
    }
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    setError(null);
    const filters: OrderFilters = {
      search: appliedSearch || undefined,
      status: ORDER_GROUP_STATUSES[group],
    };
    const result = await fetchMyOrders(filters, page, size);
    if (requestId !== latestRequest.current) {
      return;
    }
    if (result.ok && result.data) {
      setOrders({
        content: result.data.content,
        totalElements: result.data.totalElements,
      });
    } else {
      setOrders(EMPTY_PAGE);
      setError(
        result.status === 401 || result.status === 403
          ? 'unauthorized'
          : 'request',
      );
    }
    setLoading(false);
  }, [appliedSearch, group, page, size]);

  useEffect(() => {
    load();
    return () => {
      latestRequest.current += 1;
    };
  }, [load]);

  useEffect(
    () => () => {
      mounted.current = false;
    },
    [],
  );

  // The consultant's projects, to name those a waiting order unlocks;
  // requested once for the page.
  useEffect(() => {
    if (!hasWaitingOrder || projectsRequested.current) {
      return;
    }
    projectsRequested.current = true;
    fetchMyAssignedProjects().then((result) => {
      if (mounted.current && result?.ok && Array.isArray(result.data)) {
        setProjects(result.data);
      }
    });
  }, [hasWaitingOrder]);

  const loadDetails = useCallback((orderNumber: string) => {
    setDetails((current) => ({
      ...current,
      [orderNumber]: { status: 'loading' },
    }));
    fetchMyOrder(orderNumber).then((result) => {
      if (!mounted.current) {
        return;
      }
      setDetails((current) => ({
        ...current,
        [orderNumber]:
          result?.ok && result.data
            ? { status: 'ready', details: result.data }
            : { status: 'error' },
      }));
    });
  }, []);

  const forgetDetails = (orderNumber: string) =>
    setDetails((current) => {
      const rest = { ...current };
      delete rest[orderNumber];
      return rest;
    });

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === appliedSearch) {
      return undefined;
    }
    const timer = setTimeout(
      () => updateParams({ q: trimmed, page: undefined }),
      SEARCH_DELAY_MS,
    );
    return () => clearTimeout(timer);
  }, [appliedSearch, search, updateParams]);

  const runAction = async (
    order: OrderSummary,
    request: Promise<ApiResult<OrderDetails>>,
    successId: string,
  ) => {
    setBusyOrder(order.orderNumber);
    setMessage(null);
    const result = await request;
    setBusyOrder('');
    if (result.ok) {
      setMessage({ severity: 'success', id: successId });
    } else {
      setMessage({
        severity: 'error',
        id: getActionErrorMessage(result.status),
        detail: getActionErrorDetail(result.status, result.message),
      });
    }
    loadPendingCount();
    load();
    // The decided order changed (or was refused): drop its details once the
    // reload is under way, so it is loaded afresh only if still listed.
    forgetDetails(order.orderNumber);
  };

  const stop = (event: MouseEvent) => event.stopPropagation();

  const isExpanded = (order: OrderSummary) =>
    expanded[order.orderNumber] ?? isWaitingForDecision(order);

  const toggle = (order: OrderSummary) =>
    setExpanded((current) => ({
      ...current,
      [order.orderNumber]: !isExpanded(order),
    }));

  // Load the details of every open waiting order not loaded yet; the other
  // rows load their own details when opened.
  const missingDetails = loading
    ? []
    : orders.content
        .filter(
          (order) =>
            isWaitingForDecision(order) &&
            isExpanded(order) &&
            !details[order.orderNumber],
        )
        .map(({ orderNumber }) => orderNumber);
  const missingKey = missingDetails.join('|');
  useEffect(() => {
    if (missingKey) {
      missingKey.split('|').forEach(loadDetails);
    }
  }, [missingKey, loadDetails]);

  return (
    <Styles.Page>
      <PageHeader
        title={intl.formatMessage({ id: 'MY_ORDERS.TITLE' })}
        description={intl.formatMessage({ id: 'MY_ORDERS.DESCRIPTION' })}
      />

      <Paper>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gridGap={12}
          pr={2}
        >
          <OrderGroupTabs
            groups={ORDER_GROUPS}
            value={group}
            pendingCount={pendingCount}
            onChange={(value) => updateParams({ tab: value, page: undefined })}
          />
          <TextField
            label={intl.formatMessage({ id: 'ORDERS.FILTER.SEARCH' })}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            variant="outlined"
            size="small"
          />
        </Box>
      </Paper>

      {(error || message) && (
        <Box mt={2}>
          {error === 'unauthorized' && (
            <Alert severity="warning">
              {intl.formatMessage({ id: 'MY_ORDERS.UNAUTHORIZED' })}
            </Alert>
          )}
          {error === 'request' && (
            <Alert
              severity="error"
              action={
                <Button onClick={load}>
                  {intl.formatMessage({ id: 'ORDERS.RETRY' })}
                </Button>
              }
            >
              {intl.formatMessage({ id: 'MY_ORDERS.ERROR' })}
            </Alert>
          )}
          {message && (
            <Alert severity={message.severity} onClose={() => setMessage(null)}>
              {intl.formatMessage({ id: message.id })}
              {message.detail && <div>{message.detail}</div>}
            </Alert>
          )}
        </Box>
      )}

      {!error &&
        !loading &&
        orders.content
          .filter(
            (order) =>
              isPastDecisionDeadline(order) && !order.successorOrderNumber,
          )
          .map((order) => (
            <Box mt={2} key={`expired-${order.orderNumber}`}>
              <ExpiredOrderAlert order={order} consultantView />
            </Box>
          ))}

      {!error && (
        <Box mt={2} component={Paper}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={6}>
              <CircularProgress
                aria-label={intl.formatMessage({ id: 'ORDERS.LOADING' })}
              />
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table
                  size="small"
                  aria-label={intl.formatMessage({ id: 'MY_ORDERS.TITLE' })}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.NUMBER' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.CLIENT' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.PERIOD' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.DEADLINE' })}
                      </TableCell>
                      <TableCell>
                        {intl.formatMessage({ id: 'ORDERS.STATUS' })}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {!orders.content.length && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          {intl.formatMessage({ id: 'MY_ORDERS.EMPTY' })}
                        </TableCell>
                      </TableRow>
                    )}
                    {orders.content.map((order) => {
                      const detailsUrl = `/my-assignments/${order.orderNumber}`;
                      const busy = busyOrder === order.orderNumber;
                      const waiting = isWaitingForDecision(order);
                      const open = isExpanded(order);
                      return (
                        <Fragment key={order.orderNumber}>
                          <Styles.ClickableRow
                            hover
                            onClick={() => toggle(order)}
                          >
                            <Styles.NoWrapCell>
                              <RowExpandToggle
                                open={open}
                                onToggle={() => toggle(order)}
                              />{' '}
                              <Link to={detailsUrl} onClick={stop}>
                                {order.orderNumber}
                              </Link>
                            </Styles.NoWrapCell>
                            <TableCell>
                              {order.clientName}
                              {order.subject?.trim() && (
                                <Styles.SubjectNote>
                                  {order.subject.trim()}
                                </Styles.SubjectNote>
                              )}
                            </TableCell>
                            <Styles.NoWrapCell>
                              {moment(order.startDate).format('DD/MM/YYYY')} –{' '}
                              {moment(order.endDate).format('DD/MM/YYYY')}
                            </Styles.NoWrapCell>
                            <Styles.NoWrapCell>
                              {order.status === 'PENDING'
                                ? moment(order.decisionDeadline).format(
                                    'DD/MM/YYYY',
                                  )
                                : '—'}
                            </Styles.NoWrapCell>
                            <TableCell>
                              <OrderStatusCell order={order} />
                            </TableCell>
                          </Styles.ClickableRow>
                          {open && waiting && (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <WaitingOrderPanel
                                  order={order}
                                  entry={details[order.orderNumber]}
                                  onRetry={() => loadDetails(order.orderNumber)}
                                  projects={
                                    projects &&
                                    getClientProjectNames(
                                      projects,
                                      order.clientId,
                                    )
                                  }
                                  busy={busy}
                                  onAccept={() =>
                                    runAction(
                                      order,
                                      acceptMyOrder(order.orderNumber),
                                      'ORDERS.SUCCESS.ACCEPT',
                                    )
                                  }
                                  onReject={(reason) =>
                                    runAction(
                                      order,
                                      rejectMyOrder(order.orderNumber, {
                                        reason,
                                      }),
                                      'ORDERS.SUCCESS.REJECT',
                                    )
                                  }
                                />
                              </TableCell>
                            </TableRow>
                          )}
                          {open && !waiting && (
                            <TableRow>
                              <TableCell colSpan={5}>
                                <OrderRowDetails
                                  order={order}
                                  canManage={false}
                                  consultantView
                                />
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={orders.totalElements}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_event, nextPage) =>
                  updateParams({ page: nextPage || undefined })
                }
                onRowsPerPageChange={(event) =>
                  updateParams({
                    page: undefined,
                    size: Number(event.target.value),
                  })
                }
              />
            </>
          )}
        </Box>
      )}
    </Styles.Page>
  );
};

// Employment-contract employees get the no-access page (change request of
// 08.10).
const MyOrders = () => (
  <OrderAccess own>{() => <MyOrdersContent />}</OrderAccess>
);

export default MyOrders;
